import { supabase } from "@/integrations/supabase/client";

/**
 * Cache mémoire des URLs de lecture (HLS signées Bunny ou fichier d'origine).
 *
 * Pourquoi : les tokens Bunny sont valables 24 h, il est donc inutile de les
 * demander au moment précis du `play()` — cela retardait le premier octet de
 * vidéo. On résout toute une page de feed en un seul appel, en arrière-plan,
 * puis le lecteur lit l'URL de façon synchrone (ou est notifié à l'arrivée).
 */

/** Marge de sécurité avant expiration d'un lien mis en cache (2 h). */
const CACHE_TTL_MS = 2 * 60 * 60 * 1000;

interface CacheEntry {
  url: string;
  resolvedAt: number;
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<void>>();
/** Abonnés notifiés quand l'URL d'une vidéo devient disponible (résolution tardive). */
const subscribers = new Map<string, Set<(url: string) => void>>();

/** URL de lecture déjà connue, sans aucun appel réseau. */
export function getCachedPlaybackUrl(videoId: string): string | null {
  const entry = cache.get(videoId);
  if (!entry) return null;
  if (Date.now() - entry.resolvedAt > CACHE_TTL_MS) {
    cache.delete(videoId);
    return null;
  }
  return entry.url;
}

export function setCachedPlaybackUrl(videoId: string, url: string): void {
  const previous = getCachedPlaybackUrl(videoId);
  cache.set(videoId, { url, resolvedAt: Date.now() });
  if (previous === url) return;
  subscribers.get(videoId)?.forEach((listener) => listener(url));
}

/**
 * Écoute l'arrivée (ou le changement) de l'URL d'une vidéo.
 * Permet au lecteur d'être monté AVANT que les tokens ne soient résolus.
 */
export function subscribePlaybackUrl(
  videoId: string,
  listener: (url: string) => void,
): () => void {
  let set = subscribers.get(videoId);
  if (!set) {
    set = new Set();
    subscribers.set(videoId, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
    if (set && set.size === 0) subscribers.delete(videoId);
  };
}

/**
 * Résout en lot les URLs manquantes d'une liste de vidéos.
 * Best-effort : en cas d'échec, le lecteur retombe sur son URL de repli.
 */
export async function resolvePlaybackUrls(videoIds: string[]): Promise<void> {
  const missing = videoIds.filter((id) => !getCachedPlaybackUrl(id) && !inflight.has(id));
  if (missing.length === 0) return;

  // Bunny limite la taille utile d'un lot ; on découpe par paquets de 25.
  const batches: string[][] = [];
  for (let i = 0; i < missing.length; i += 25) {
    batches.push(missing.slice(i, i + 25));
  }

  await Promise.all(
    batches.map((batch) => {
      const promise = (async () => {
        try {
          const { data, error } = await supabase.functions.invoke("get-videos-playback", {
            body: { videoIds: batch },
          });
          if (error || !data?.urls) return;
          Object.entries(data.urls as Record<string, string>).forEach(([id, url]) => {
            if (typeof url === "string" && url) setCachedPlaybackUrl(id, url);
          });
        } catch {
          // Silencieux : repli géré par le lecteur.
        } finally {
          batch.forEach((id) => inflight.delete(id));
        }
      })();
      batch.forEach((id) => inflight.set(id, promise));
      return promise;
    }),
  );
}

/**
 * Prépare l'ouverture d'un lecteur hors feed (recherche, profil) :
 * 1. signe d'abord la SEULE vidéo que l'on va jouer, puis réchauffe son premier
 *    fragment — c'est ce qui supprime l'attente avant le play ;
 * 2. lance la signature du reste de la liste sans l'attendre.
 *
 * `fallbackUrl` sert quand la signature échoue (flux HLS déjà connu côté base).
 * Jamais plus d'un fragment par vidéo.
 */
export async function preparePlayback(
  videoIds: string[],
  playFirstId: string,
  fallbackUrl?: string | null,
): Promise<void> {
  // Chargement normal : on signe UNIQUEMENT la vidéo que l'on va jouer.
  // Aucun fragment réchauffé, aucune autre vidéo de la liste résolue.
  await resolvePlaybackUrls([playFirstId]);
}

/** Connexion lente ou mode économie de données : on limite le préchargement. */
export function isDataSaverMode(): boolean {
  const connection = (navigator as any)?.connection;
  if (!connection) return false;
  if (connection.saveData) return true;
  return ["2g", "slow-2g"].includes(connection.effectiveType);
}
